import { AnswerType } from "api/generated/schemas/answerType";
import { AnswerOptionIcon } from "api/generated/schemas/answerOptionIcon";
import type { AnswerOption } from "meetings/shared/types/answerType.types";

export interface AnswerTypeDefinition {
  value: AnswerType;
  label: string;
  sublabel: string;
  icon: string;
  maxOptions: number;
}

export const ANSWER_TYPE_DEFINITIONS: AnswerTypeDefinition[] = [
  {
    value: AnswerType.three_option,
    label: "Stoplicht",
    sublabel: "(3 opties)",
    icon: "fa-traffic-light",
    maxOptions: 3,
  },
  {
    value: AnswerType.two_option,
    label: "Stoplicht",
    sublabel: "(2 opties)",
    icon: "fa-traffic-light",
    maxOptions: 2,
  },
  {
    value: AnswerType.upstream,
    label: "Opstromen",
    sublabel: "",
    icon: "fa-arrow-up-from-ground-water",
    maxOptions: 4,
  },
];

export const DEFAULT_ANSWER_OPTIONS: Record<AnswerType, AnswerOption[]> = {
  [AnswerType.two_option]: [
    { position: 1, label: "Bevorderen", icon: AnswerOptionIcon.green },
    { position: 2, label: "Niet bevorderen", icon: AnswerOptionIcon.red },
  ],
  [AnswerType.three_option]: [
    { position: 1, label: "Bevorderen", icon: AnswerOptionIcon.green },
    { position: 2, label: "Twijfel", icon: AnswerOptionIcon.orange },
    { position: 3, label: "Niet bevorderen", icon: AnswerOptionIcon.red },
  ],
  [AnswerType.upstream]: [
    { position: 1, label: "Opstromen", icon: AnswerOptionIcon.arrow_up_right },
    { position: 2, label: "Bevorderen", icon: AnswerOptionIcon.arrow_right },
    { position: 3, label: "Twijfel", icon: AnswerOptionIcon.arrow_back_left },
    {
      position: 4,
      label: "Niet bevorderen",
      icon: AnswerOptionIcon.arrow_back_left_alt,
    },
  ],
};

/**
 * AnswerOptionIcon values that render as a plain coloured circle instead
 * of a Font Awesome icon. Use isCircleAnswerOptionIcon() to check.
 */
export const CIRCLE_ANSWER_OPTION_ICONS: ReadonlySet<AnswerOptionIcon> =
  new Set([
    AnswerOptionIcon.green,
    AnswerOptionIcon.orange,
    AnswerOptionIcon.red,
  ]);

export const isCircleAnswerOptionIcon = (
  icon: AnswerOptionIcon | null | undefined,
): boolean => (icon ? CIRCLE_ANSWER_OPTION_ICONS.has(icon) : false);

/**
 * Maps AnswerOptionIcon enum values to Font Awesome icon names.
 */
export const ANSWER_OPTION_FA_ICONS: Record<AnswerOptionIcon, string> = {
  [AnswerOptionIcon.green]: "fa-circle",
  [AnswerOptionIcon.orange]: "fa-circle",
  [AnswerOptionIcon.red]: "fa-circle",
  [AnswerOptionIcon.arrow_up_right]: "fa-arrow-trend-up",
  [AnswerOptionIcon.arrow_right]: "fa-arrow-right",
  [AnswerOptionIcon.arrow_back_left]: "fa-right-left",
  [AnswerOptionIcon.arrow_back_left_alt]: "fa-arrow-rotate-left",
  [AnswerOptionIcon.thumbs_up]: "fa-thumbs-up",
  [AnswerOptionIcon.thumbs_down]: "fa-thumbs-down",
};

/**
 * Semantic palette name for an AnswerOptionIcon. Used by TrafficLightButton
 * to derive idle/active Tailwind classes for its dot and ring.
 */
export type AnswerOptionPalette = "green" | "orange" | "red" | "blue";

export const ANSWER_OPTION_PALETTES: Record<
  AnswerOptionIcon,
  AnswerOptionPalette
> = {
  [AnswerOptionIcon.green]: "green",
  [AnswerOptionIcon.orange]: "orange",
  [AnswerOptionIcon.red]: "red",
  [AnswerOptionIcon.arrow_up_right]: "blue",
  [AnswerOptionIcon.arrow_right]: "green",
  [AnswerOptionIcon.arrow_back_left]: "orange",
  [AnswerOptionIcon.arrow_back_left_alt]: "red",
  [AnswerOptionIcon.thumbs_up]: "green",
  [AnswerOptionIcon.thumbs_down]: "red",
};

/**
 * Tailwind class lookups keyed by AnswerOptionPalette. Each entry uses the
 * 300 shade for the idle / outer-ring tint and the 500 shade for the active
 * / inner-dot fill, so the rule "lighter when idle, darker when selected"
 * stays in one place. Class strings are literal so Tailwind JIT picks them
 * up without a safelist.
 *
 * The 'orange' palette maps to the project's `yellow` Tailwind colour set
 * because that's where the design system's amber tokens live
 * (--color-yellow-300/500 etc.). `tw-bg-orange-*` would fall through to
 * Tailwind's default orange and ignore the project CSS variables.
 */
export const PALETTE_BG: Record<
  AnswerOptionPalette,
  { idle: string; active: string }
> = {
  green: { idle: "tw-bg-green-300", active: "tw-bg-green-500" },
  orange: { idle: "tw-bg-yellow-300", active: "tw-bg-yellow-500" },
  red: { idle: "tw-bg-red-300", active: "tw-bg-red-500" },
  blue: { idle: "tw-bg-blue-300", active: "tw-bg-blue-500" },
};

/**
 * Border colour applied to the outer ring of a selected TrafficLightButton.
 * Single value per palette — when the button is unselected the border is
 * always neutral (gray-200), so a separate "active" shade isn't read.
 */
export const PALETTE_BORDER: Record<AnswerOptionPalette, string> = {
  green: "tw-border-green-300",
  orange: "tw-border-yellow-300",
  red: "tw-border-red-300",
  blue: "tw-border-blue-300",
};

/**
 * Active background for TrafficLightButton's "rating" variant — the softer
 * 200 shade + shadow used by RatingToggle (see RATING_LEVELS.activeBoxClass).
 */
export const PALETTE_RATING_ACTIVE_BG: Record<AnswerOptionPalette, string> = {
  green: "tw-bg-green-200 tw-shadow-xs",
  orange: "tw-bg-yellow-200 tw-shadow-xs",
  red: "tw-bg-red-200 tw-shadow-xs",
  blue: "tw-bg-blue-200 tw-shadow-xs",
};

export const PALETTE_TEXT: Record<
  AnswerOptionPalette,
  { idle: string; active: string }
> = {
  green: { idle: "tw-text-green-300", active: "tw-text-green-500" },
  orange: { idle: "tw-text-yellow-300", active: "tw-text-yellow-500" },
  red: { idle: "tw-text-red-300", active: "tw-text-red-500" },
  blue: { idle: "tw-text-blue-300", active: "tw-text-blue-500" },
};

/**
 * Maps AnswerOptionIcon to active background/border Tailwind classes.
 */
export const ANSWER_OPTION_ACTIVE_CLASSES: Record<AnswerOptionIcon, string> = {
  [AnswerOptionIcon.green]: "tw-border-[#afecc5] tw-bg-[#ddf4e5]",
  [AnswerOptionIcon.orange]: "tw-border-[#ffd69b] tw-bg-[#ffeede]",
  [AnswerOptionIcon.red]: "tw-border-[#f7d3d0] tw-bg-[#fbe8e4]",
  [AnswerOptionIcon.arrow_up_right]: "tw-border-[#add4ff] tw-bg-[#daeaff]",
  [AnswerOptionIcon.arrow_right]: "tw-border-[#afecc5] tw-bg-[#ddf4e5]",
  [AnswerOptionIcon.arrow_back_left]: "tw-border-[#ffd69b] tw-bg-[#ffeede]",
  [AnswerOptionIcon.arrow_back_left_alt]: "tw-border-[#f7d3d0] tw-bg-[#fbe8e4]",
  [AnswerOptionIcon.thumbs_up]: "tw-border-[#afecc5] tw-bg-[#ddf4e5]",
  [AnswerOptionIcon.thumbs_down]: "tw-border-[#f7d3d0] tw-bg-[#fbe8e4]",
};

export const ANSWER_OPTION_ICON_COLORS: Record<AnswerOptionIcon, string> = {
  [AnswerOptionIcon.green]: "#60B67F",
  [AnswerOptionIcon.orange]: "#FCB653",
  [AnswerOptionIcon.red]: "#EC6244",
  [AnswerOptionIcon.arrow_up_right]: "#4294EE",
  [AnswerOptionIcon.arrow_right]: "#60B67F",
  [AnswerOptionIcon.arrow_back_left]: "#FCB653",
  [AnswerOptionIcon.arrow_back_left_alt]: "#EC6244",
  [AnswerOptionIcon.thumbs_up]: "#60B67F",
  [AnswerOptionIcon.thumbs_down]: "#EC6244",
};
